export const PREVIEW_CONCURRENCY = 4;

const RASTER_IMAGE_MIMES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
]);

export function previewKey(req) {
  return `${req.tenant_id}:${req.entity_id}`;
}

export function isRasterImageMime(mime) {
  return typeof mime === 'string' && RASTER_IMAGE_MIMES.has(mime.toLowerCase());
}

export function isPdfMime(mime) {
  return typeof mime === 'string' && mime.toLowerCase() === 'application/pdf';
}

export function isPreviewableMime(mime) {
  return isPdfMime(mime) || isRasterImageMime(mime);
}

export function pickInvoiceFile(attachments) {
  const list = Array.isArray(attachments) ? attachments : [];
  return list.find((file) => isPdfMime(file.mime))
    || list.find((file) => isRasterImageMime(file.mime))
    || null;
}

function bytesMatch(buf, signature) {
  if (buf.length < signature.length) return false;
  return signature.every((byte, index) => buf[index] === byte);
}

export function sniffPreviewKind(bytes, claimedMime) {
  const buf = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (isPdfMime(claimedMime)) {
    const head = new TextDecoder().decode(buf.slice(0, 5));
    return head.startsWith('%PDF') ? 'pdf' : null;
  }
  if (!isRasterImageMime(claimedMime)) return null;
  if (bytesMatch(buf, [0xFF, 0xD8, 0xFF])) return 'image';
  if (bytesMatch(buf, [0x89, 0x50, 0x4E, 0x47])) return 'image';
  if (bytesMatch(buf, [0x47, 0x49, 0x46])) return 'image';
  if (
    buf.length >= 12
    && bytesMatch(buf, [0x52, 0x49, 0x46, 0x46])
    && buf[8] === 0x57
    && buf[9] === 0x45
    && buf[10] === 0x42
    && buf[11] === 0x50
  ) {
    return 'image';
  }
  return null;
}

export function rasterMimeForKind(claimedMime) {
  const mime = typeof claimedMime === 'string' ? claimedMime.toLowerCase() : '';
  return mime === 'image/jpg' ? 'image/jpeg' : mime;
}

export async function inspectAttachmentBlob(blob, claimedMime) {
  if (!blob || !isPreviewableMime(claimedMime)) return null;
  const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  const kind = sniffPreviewKind(head, claimedMime);
  if (!kind) return null;
  const mime = kind === 'pdf' ? 'application/pdf' : rasterMimeForKind(claimedMime);
  const typed = blob.type === mime ? blob : new Blob([blob], { type: mime });
  return { kind, mime, blob: typed };
}

export function uniquePurchaseInvoiceTargets(rows) {
  const seen = new Set();
  const targets = [];
  (Array.isArray(rows) ? rows : []).forEach((req) => {
    if (req.entity_type !== 'purchase_invoice' || !req.tenant_id || !req.entity_id) return;
    const key = previewKey(req);
    if (seen.has(key)) return;
    seen.add(key);
    targets.push(req);
  });
  return targets;
}

function attachmentList(res) {
  return res?.data?.attachments || (Array.isArray(res?.data) ? res.data : []);
}

export async function loadInvoiceThumbnail(req, attachmentsAPI) {
  const res = await attachmentsAPI.getAll(req.tenant_id, {
    link_id: req.entity_id,
    attachment_type: 'p_invoice',
    limit: 20,
  });
  const file = pickInvoiceFile(attachmentList(res));
  if (!file?.id) return null;
  if (isPdfMime(file.mime)) {
    return {
      key: previewKey(req),
      kind: 'pdf',
      mime: 'application/pdf',
      tenantId: req.tenant_id,
      attachmentId: file.id,
    };
  }
  const fileRes = await attachmentsAPI.getFile(req.tenant_id, file.id, true);
  const inspected = await inspectAttachmentBlob(fileRes.data, file.mime);
  if (!inspected || inspected.kind !== 'image') return null;
  return {
    key: previewKey(req),
    kind: 'image',
    mime: inspected.mime,
    url: URL.createObjectURL(inspected.blob),
    tenantId: req.tenant_id,
    attachmentId: file.id,
  };
}

export async function loadInvoiceFullPreview(preview, attachmentsAPI) {
  if (!preview?.attachmentId || !preview.tenantId) return null;
  if (preview.url) return preview;
  const fileRes = await attachmentsAPI.getFile(preview.tenantId, preview.attachmentId, false);
  const inspected = await inspectAttachmentBlob(fileRes.data, preview.mime);
  if (!inspected) return null;
  return {
    ...preview,
    kind: inspected.kind,
    mime: inspected.mime,
    url: URL.createObjectURL(inspected.blob),
  };
}

export function revokePreviewUrls(preview) {
  if (preview?.url) URL.revokeObjectURL(preview.url);
}

// A download can finish after the page is gone. Nothing would revoke its URL then.
export function keepIfActive(preview, isActive) {
  if (isActive()) return preview;
  revokePreviewUrls(preview);
  return null;
}

/**
 * Queue for row thumbnails. A row calls ``request`` when it scrolls into view, so a
 * long "Show completed" list only fetches what the approver actually sees. At most
 * ``concurrency`` loads run at once and each preview is published as it arrives.
 */
export function createPreviewLoader(attachmentsAPI, { concurrency = PREVIEW_CONCURRENCY, onPreview } = {}) {
  const requested = new Set();
  let queue = [];
  let active = 0;
  let stopped = false;

  function pump() {
    while (!stopped && active < concurrency && queue.length) {
      const req = queue.shift();
      active += 1;
      loadInvoiceThumbnail(req, attachmentsAPI)
        .then((preview) => {
          if (!preview) return;
          if (stopped || !requested.has(preview.key)) {
            revokePreviewUrls(preview);
            return;
          }
          onPreview?.(preview.key, preview);
        })
        .catch(() => {
          // The row still lists the approval when the invoice file cannot be loaded.
        })
        .finally(() => {
          active -= 1;
          pump();
        });
    }
  }

  return {
    request(req) {
      if (stopped || req?.entity_type !== 'purchase_invoice' || !req.tenant_id || !req.entity_id) return;
      const key = previewKey(req);
      if (requested.has(key)) return;
      requested.add(key);
      queue.push(req);
      pump();
    },
    // The list changed: drop rows that left it so they load again if they return.
    retain(keys) {
      Array.from(requested).forEach((key) => {
        if (!keys.has(key)) requested.delete(key);
      });
      queue = queue.filter((req) => keys.has(previewKey(req)));
    },
    stop() {
      stopped = true;
      queue = [];
    },
  };
}
